import { CELL, GROUND } from '../core/config';
import { cargoAirframe } from './cargo-aircraft';
import { createArchetype, key, MATERIALS, type Building, type Cell, type City, type Place } from './city';
import type { Vec } from '../destruction/structure';
import { roadSegment } from './roads';

export const APRON_Y = GROUND + 0.12;
export interface AirportLayout {
  groundDepot: { role: 'fuel' | 'baggage'; home: Vec; supply: Vec; cell: number }[];
  name: string;
  terminal: number;
  runway: { west: Vec; east: Vec; sections: number[]; supports: number[] };
  stands: Vec[];
  gate: Place[];
  exit: Vec;
  walkway: Vec[];
  footprint: { x: number; z: number; width: number; depth: number };
  serviceNode: number;
  serviceBay: Vec;
}
export function putCell(
  b: Building,
  x: number,
  y: number,
  z: number,
  material: Cell['material'],
  role: Cell['role'],
  color: number,
  bounds?: Cell['bounds'],
): void {
  b.cells.push({
    id: b.id * 1e6 + key(x, y, z),
    x,
    y,
    z,
    material,
    role,
    color,
    hp: MATERIALS[material].strength,
    root: y === 0,
    bounds,
  });
}
/** Airport occupies a documented eastern extension; useful urban area remains 1.59 km². */
export function prepareAirport(city: City): City {
  if (!city.regional || city.airport) return city;
  let serial =
    Math.max(
      ...[...city.buildings, ...city.props!, ...city.bridges!, ...(city.infrastructure ?? [])].map(
        (b) => b.id,
      ),
    ) + 1;
  const terminal = createArchetype(serial++, 370, -390, 'market-hall', 30, 18, 2, 0xcbd3c8, 0x7daab1);
  terminal.name = 'Alder Airport · terminal and concourse';
  terminal.district = 'airport';
  // A real airside door complements the public entrance created by the interior builder.
  terminal.cells = terminal.cells.filter((c) => !(c.x === 29 && [1, 2].includes(c.z) && c.y > 0 && c.y < 4));
  city.buildings.push(terminal);
  const hangar = createArchetype(serial++, 610, -452, 'hangar', 30, 26, 2, 0xb8c5bd, 0x6b8c98);
  hangar.name = 'Alder aircraft maintenance hangar';
  hangar.district = 'airport';
  hangar.cells = hangar.cells.filter((c) => !(c.z === 0 && c.x > 3 && c.x < 26 && c.y > 0 && c.y < 6));
  city.buildings.push(hangar);
  const tower = createArchetype(serial++, 370, -458, 'point-tower', 7, 7, 5, 0xc7c9bc, 0x648e9a);
  tower.name = 'Alder control tower';
  tower.district = 'airport';
  city.buildings.push(tower);
  const airport: AirportLayout = {
    groundDepot: [],
    name: 'Alder Regional Airport',
    terminal: terminal.id,
    runway: {
      west: { x: 410, y: APRON_Y, z: -580 },
      east: { x: 1120, y: APRON_Y, z: -580 },
      sections: [],
      supports: [],
    },
    stands: [
      { x: 505, y: APRON_Y, z: -412 },
      { x: 558, y: APRON_Y, z: -412 },
    ],
    gate: [],
    exit: { x: terminal.x + 30.5 * CELL, y: GROUND + CELL, z: terminal.z + 1.5 * CELL },
    walkway: [
      { x: 411, y: APRON_Y, z: -388.125 },
      { x: 420, y: APRON_Y, z: -379 },
      { x: 505, y: APRON_Y, z: -379 },
      { x: 558, y: APRON_Y, z: -379 },
    ],
    footprint: { x: 355, z: -610, width: 790, depth: 270 },
    serviceNode: 0,
    serviceBay: { x: 663, y: GROUND + 0.2, z: -420 },
  };
  city.airport = airport;
  city.half = Math.max(city.half, 1180);
  city.infrastructure ??= [];
  const graph = city.roadGraph!,
    nodes = new Map(graph.nodes.map((n) => [`${n.x},${n.z}`, n.id]));
  const node = (x: number, z: number) => {
    const id = nodes.get(`${x},${z}`);
    if (id !== undefined) return id;
    const next = graph.nodes.length;
    graph.nodes.push({ id: next, x, z, y: GROUND + 0.2, layer: 'street' });
    nodes.set(`${x},${z}`, next);
    return next;
  };
  const road = (x: number, z: number, X: number, Z: number) => {
    const length = Math.hypot(X - x, Z - z),
      parts = Math.ceil(length / 30);
    for (let i = 0; i < parts; i++) {
      const p = { x: x + ((X - x) * i) / parts, y: GROUND + 0.2, z: z + ((Z - z) * i) / parts },
        q = { x: x + ((X - x) * (i + 1)) / parts, y: GROUND + 0.2, z: z + ((Z - z) * (i + 1)) / parts };
      const edge = {
        id: graph.edges.length,
        from: node(p.x, p.z),
        to: node(q.x, q.z),
        width: 7,
        kind: 'airport service',
        profile: [p, q],
        maxGrade: 0,
        layer: 'street',
        surfaceIds: [] as number[],
      };
      graph.edges.push(edge);
      const surface = roadSegment(serial++, edge, p, q, 4.5);
      edge.surfaceIds.push(surface.id);
      city.infrastructure!.push(surface);
    }
  };
  for (const [a, b] of [
    [350, 470],
    [470, 600],
    [600, 700],
    [700, 790],
    [790, 920],
    [920, 1090],
  ])
    road(a, -310, b, -310);
  road(700, -310, 700, -400);
  road(700, -400, 660, -400);
  road(660, -400, 660, -435);
  road(790, -310, 790, -385);
  road(790, -385, 790, -470);
  airport.serviceNode = node(660, -435);
  const paving = (name: string, x: number, z: number, width: number, depth: number, color: number) => {
    const b: Building = {
      id: serial++,
      name,
      kind: 'road',
      category: 'prop',
      district: 'airport',
      x,
      z,
      w: Math.ceil(width / CELL),
      d: Math.ceil(depth / CELL),
      h: 1,
      color,
      accent: color,
      cells: [],
    };
    for (let xx = 0; xx < b.w; xx++)
      for (let zz = 0; zz < b.d; zz++)
        putCell(b, xx, 0, zz, 'concrete', 'slab', color, {
          x: 0,
          y: 0,
          z: 0,
          w: Math.min(1, width / CELL - xx),
          h: 0.12 / CELL,
          d: Math.min(1, depth / CELL - zz),
        });
    city.infrastructure!.push(b);
    return b;
  };
  for (let x = 390; x < 1140; x += 30) {
    const b = paving('Alder runway 09 / 27', x, -593, Math.min(30, 1140 - x), 26, 0x647472);
    airport.runway.sections.push(b.id);
    for (const c of b.cells) {
      if (c.z === 10) {
        airport.runway.supports.push(c.id);
        if (c.x < 12) c.color = 0xe9e4cf;
      }
      if ((x < 420 || x >= 1110) && c.x % 4 < 2 && c.z > 2 && c.z < 18) c.color = 0xe6e5d8;
    }
  }
  for (let x = 440; x < 630; x += 30) paving('Alder taxiway', x, -510, Math.min(30, 630 - x), 13, 0x80928a);
  for (let z = -567; z < -510; z += 28)
    paving('Runway exit taxiway', 443, z, 13, Math.min(28, -510 - z), 0x80928a);
  for (let z = -497; z < -396; z += 28)
    for (let x = 440; x < 632; x += 30)
      paving('Alder apron', x, z, Math.min(30, 632 - x), Math.min(28, -396 - z), 0xa7b5a7);
  for (let x = 406; x < 565; x += 30)
    paving('Terminal passenger walk', x, -381, Math.min(30, 565 - x), 4, 0xd0cebb);
  for (const stand of airport.stands) {
    paving('Boarding walk', stand.x - 6, -420, 3, 43, 0xd0cebb);
    const stair: Building = {
      id: serial++,
      name: 'Passenger boarding stairs',
      kind: 'road',
      category: 'prop',
      district: 'airport',
      x: stand.x - 3.5,
      z: stand.z - 6,
      w: 2,
      d: 2,
      h: 2,
      color: 0xc9cabc,
      accent: 0xc9cabc,
      cells: [],
    };
    for (let s = 0; s < 6; s++)
      for (let z = 0; z < 2; z++)
        putCell(stair, s, 0, z, 'metal', 'slab', 0xc9cabc, {
          x: s * (0.3 / CELL - 1),
          y: 0,
          z: 0,
          w: 0.3 / CELL,
          h: ((APRON_Y - GROUND + 0.035 + 1.475) * (s + 1)) / 6 / CELL,
          d: 1,
        });
    city.props!.push(stair);
  }
  for (const [i, role] of (['fuel', 'baggage'] as const).entries()) {
    const home = { x: 610 + i * 12, y: APRON_Y, z: -480 };
    const equipment: Building = {
      id: serial++,
      name: role === 'fuel' ? 'Alder fuel service cabinet' : 'Alder baggage dispatch rack',
      kind: 'furniture',
      category: 'prop',
      district: 'airport',
      x: home.x + 4.5,
      z: home.z + 6,
      w: 2,
      d: 1,
      h: 2,
      color: 0xd0ba84,
      accent: 0x647d78,
      cells: [],
      baseY: APRON_Y,
    };
    putCell(equipment, 0, 0, 0, 'metal', 'prop', 0xc4ba96, { x: 0, y: 0, z: 0, w: 0.8, h: 1.1, d: 0.5 });
    putCell(equipment, 1, 0, 0, 'metal', 'prop', 0x697f7c, { x: 0, y: 0, z: 0, w: 0.18, h: 1.25, d: 0.5 });
    city.props!.push(equipment);
    airport.groundDepot.push({
      role,
      home,
      supply: { x: home.x + 4.95, y: APRON_Y, z: home.z + 5.25 },
      cell: equipment.cells[0].id,
    });
  }
  const steps: Building = {
    id: serial++,
    name: 'Terminal airside steps',
    kind: 'road',
    category: 'prop',
    x: 407.5,
    z: -389.25,
    w: 2,
    d: 2,
    h: 1,
    color: 0xc9cabc,
    accent: 0xc9cabc,
    cells: [],
  };
  for (let s = 0; s < 5; s++)
    for (let z = 0; z < 2; z++)
      putCell(steps, s, 0, z, 'concrete', 'slab', 0xc9cabc, {
        x: s * (0.3 / CELL - 1),
        y: 0,
        z: 0,
        w: 0.3 / CELL,
        h: (1.25 - s * 0.25) / CELL,
        d: 1,
      });
  city.props!.push(steps);
  for (let i = 0; i < 10; i++) {
    const x = 18 + (i % 5) * 2,
      z = 7 + Math.floor(i / 5) * 3;
    airport.gate.push({
      id: `airport-gate:${i}`,
      kind: 'gate',
      name: 'Alder departures · regional connection',
      buildingId: terminal.id,
      floor: 0,
      x: terminal.x + (x + 0.5) * CELL,
      y: GROUND + CELL,
      z: terminal.z + (z + 0.5) * CELL,
      supportCell: terminal.id * 1e6 + key(x, 0, z),
      capacity: 1,
      duration: 0,
    });
  }
  return city;
}

/** Every aircraft part is an ordinary cell from takeoff through fracture. */
export function airframe(
  id: number,
  family: 'regional-jet' | 'twin-prop' | 'helicopter' | 'cargo-transport',
  troopInterior = false,
): Building {
  if (family === 'cargo-transport') return cargoAirframe(id, troopInterior);
  const b: Building = {
    id,
    name:
      family === 'helicopter'
        ? 'Service helicopter'
        : family === 'regional-jet'
          ? 'Alder regional jet'
          : 'Alder twin prop',
    kind: 'aircraft',
    category: 'prop',
    x: 0,
    z: 0,
    w: 28,
    d: 25,
    h: 8,
    color: 0xd6dfd8,
    accent: 0x6c9c98,
    cells: [],
  };
  const cells = new Map<number, Cell>();
  const add = (
    x: number,
    y: number,
    z: number,
    color: number,
    bounds?: Cell['bounds'],
    material: Cell['material'] = 'metal',
  ) => {
    const c: Cell = {
      id: id * 1e6 + key(x, y, z),
      x,
      y,
      z,
      material,
      role: 'prop',
      color,
      hp: MATERIALS[material].strength,
      bounds,
      root: false,
    };
    cells.set(key(x, y, z), c);
  };
  const heli = family === 'helicopter',
    length = heli ? 10 : family === 'twin-prop' ? 20 : 24,
    start = heli ? 5 : 0;
  for (let z = start; z < length; z++) {
    const r = heli
      ? 1.75
      : Math.max(0.3, 1.65 * Math.sqrt(Math.max(0, 1 - ((z - length / 2) / (length / 2 + 0.5)) ** 2)));
    for (let x = 12; x <= 15; x++)
      for (let y = 1; y < 4; y++) {
        const left = Math.max(x * CELL, 14 * CELL - r),
          right = Math.min((x + 1) * CELL, 14 * CELL + r);
        if (right <= left) continue;
        const edge = x === 12 || x === 15;
        if (!heli && z > 2 && z < length - 3 && y === 2 && !edge) continue;
        if (!heli && x === 12 && (z === 6 || z === 7) && y < 3) continue;
        add(
          x,
          y,
          z,
          y === 2 && edge && z > 2 && z < length - 3 ? 0x6291a0 : 0xd6dfd8,
          { x: (left - x * CELL) / CELL, y: 0, z: 0, w: (right - left) / CELL, h: y === 3 ? 0.28 : 1, d: 1 },
          y === 2 && edge && z > 2 && z < length - 3 ? 'glass' : 'metal',
        );
        if (!heli && y === 1 && !edge && z > 2 && z < length - 3) cells.get(key(x, y, z))!.bounds!.h = 0.18;
      }
  }
  if (heli) {
    for (let z = 10; z < 23; z++) add(14, 2, z, 0x6c9c98, { x: 0.2, y: 0.15, z: 0, w: 0.45, h: 0.45, d: 1 });
    for (let y = 3; y < 5; y++) add(14, y, 8, 0x536d72, { x: 0.35, y: 0, z: 0.35, w: 0.3, h: 1, d: 0.3 });
    for (let x = 7; x < 22; x++) add(x, 5, 8, 0x536d72, { x: 0, y: 0, z: 0.4, w: 1, h: 0.08, d: 0.2 });
    for (let z = 1; z < 16; z++) add(14, 5, z, 0x536d72, { x: 0.4, y: 0, z: 0, w: 0.2, h: 0.08, d: 1 });
    for (const x of [12, 15])
      for (let z = 5; z < 11; z++) add(x, 0, z, 0x56615e, { x: 0.2, y: 0, z: 0, w: 0.18, h: 1, d: 1 });
  } else {
    for (let x = family === 'twin-prop' ? 4 : 1; x < (family === 'twin-prop' ? 24 : 27); x++) {
      const sweep = Math.floor(Math.abs(x - 14) * 0.24),
        chord = Math.max(1, 4 - Math.floor(Math.abs(x - 14) / 4));
      for (let z = 9 + sweep; z < 9 + sweep + chord; z++)
        if (x < 12 || x > 15) add(x, 2, z, 0xa9c2b7, { x: 0, y: 0.32, z: 0, w: 1, h: 0.16, d: 1 });
    }
    for (const x of [12, 15]) for (let z = 9; z < 14; z++) add(x, 2, z, 0xa9c2b7);
    for (const x of [9, 18])
      for (let z = 10; z < 14; z++) add(x, 2, z, 0xa9c2b7, { x: 0.1, y: 0, z: 0, w: 0.8, h: 0.48, d: 1 });
    for (let x = 9; x < 20; x++)
      for (let z = length - 4; z < length - 1; z++)
        if (x < 12 || x > 15) add(x, 3, z, 0x6c9c98, { x: 0, y: 0.25, z: 0, w: 1, h: 0.14, d: 1 });
    for (let y = 4; y < 8; y++)
      for (let z = length - 5 + (y - 4); z < length - 1; z++)
        add(14, y, z, 0x6c9c98, { x: 0.25, y: 0, z: 0, w: 0.35, h: 1, d: 1 });
    for (const x of [9, 18])
      for (let z = 10; z < 14; z++) add(x, 1, z, 0x7e9994, { x: 0.1, y: 0.32, z: 0, w: 0.8, h: 0.95, d: 1 });
    for (const [x, z] of [
      [13, 4],
      [12, 15],
      [15, 15],
    ])
      add(x, 0, z, 0x3e4e4c, { x: 0.2, y: 0, z: 0.2, w: 0.45, h: 1, d: 0.45 });
  }
  b.cells = [...cells.values()];
  return b;
}
